import React, {Component, Fragment} from 'react';
import {Card, Form, FormFeedback, FormGroup, Input, Label} from 'reactstrap';
import withValidator from '../../models/form/withValidator';
import LoadingButton from '../../../shared/ui/LoadingButton';
import './RatingMultiFormCard.scss'
import Checkbox from "../../../shared/forms/Checkbox";

const DEFAULT_SCORE_OVERRIDES = {
  1: 'Learning',
  2: 'Knowing',
  3: 'Applying'
};

const validatorConfig = {
  cssClasses: {
    fieldInvalid: 'is-invalid',
    fieldValid: 'is-valid',
  },
  fields: {
    ratingPrompt: {
      rules: [
        {
          feedback: 'Rating prompt is required',
          test(value) {
            return !!value;
          },
        },
      ],
    },
    scale: {
      rules: [
        {
          feedback: 'Scale must be between 1 and 10',
          test(value) {
            const scale = parseInt(value, 10);
            return scale >= 1 && scale <= 10;
          },
        },
      ],
    },
    notResponsivePrompt: {rules: []},
    notObservedPrompt: {rules: []},
    useNotObserved: {rules: []},
    useNotResponsive: {rules: []}
  },
};

class EvaluationRatingFormCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cssValidationClasses: {
        ratingPrompt: '',
        scale: '',
        notResponsivePrompt: '',
        notObservedPrompt: ''
      },
      errorFeedback: {},
      isFormDisabled: true,
      ratingPrompt: !this.props.initialValues ? '' : this.props.initialValues.ratingPrompt,
      scale: !this.props.initialValues ? 3 : (this.props.initialValues.scale || 3),
      notResponsivePrompt: !this.props.initialValues ? 'N/R' : (this.props.initialValues.notResponsivePrompt || 'N/R'),
      notObservedPrompt: !this.props.initialValues ? 'N/O' : (this.props.initialValues.notObservedPrompt || 'N/O'),
      defaultComments: !this.props.initialValues ? [] : this.props.initialValues.defaultComments || [],
      requiredComments: !this.props.initialValues ? [] : this.props.initialValues.requiredComments || [],
      scoreOverrides: !this.props.initialValues ? DEFAULT_SCORE_OVERRIDES : (this.props.initialValues.scoreOverrides || DEFAULT_SCORE_OVERRIDES),
      useNotObserved: !this.props.initialValues ? true : this.props.initialValues.useNotObserved !== false && this.props.initialValues.useNoNr !== false,
      useNotResponsive: !this.props.initialValues ? true : this.props.initialValues.useNotResponsive !== false && this.props.initialValues.useNoNr !== false,
    };
  }

  componentDidMount() {
    if (this.props.initialValues) {
      this.props.initialize({
        ratingPrompt: this.state.ratingPrompt,
        scale: this.state.scale,
        notResponsivePrompt: this.state.notResponsivePrompt,
        notObservedPrompt: this.state.notObservedPrompt,
        useNotObserved: this.state.useNotObserved,
        useNotResponsive: this.state.useNotResponsive
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      cssValidationClasses: nextProps.cssValidationClasses,
      errorFeedback: nextProps.errorFeedback,
      isFormDisabled: nextProps.hasErrors || nextProps.hasUntouched,
    });
  }

  get scores() {
    return Array.from(Array(parseInt(this.state.scale || 3, 10)).keys()).map(score => score + 1);
  }

  handleBlur(event) {
    const {name, value} = event.target;
    this.props.touch(name);
    this.props.validateField(name, value, true);
  }

  handleInput(event) {
    const {name, value} = event.target;
    this.setState({[name]: value}, () => this.props.validateField(name, value, true));
  }

  addDefaultComment() {
    this.setState({
      defaultComments: [...this.state.defaultComments, {rating: '', comment: ''}]
    })
  }

  removeDefaultComment(index) {
    this.setState({
      defaultComments: this.state.defaultComments.filter((dfc, i) => i !== index)
    })
  }

  updateDefaultCommentRating(index, val) {
    this.setState({
      defaultComments: this.state.defaultComments.map((dfc, i) => i !== index ? dfc : {...dfc, rating: parseInt(val, 10)})
    })
  }

  updateDefaultCommentComment(index, val) {
    this.setState({
      defaultComments: this.state.defaultComments.map((dfc, i) => i !== index ? dfc : {...dfc, comment: val})
    })
  }

  isRequiredCommentChecked(score) {
    return !!this.state.requiredComments.find(rc => rc === score);
  }

  onRequiredCommentCheck(score, checked) {
    this.setState({
      requiredComments: checked
        ? [...this.state.requiredComments.filter(rc => rc !== score), score]
        : this.state.requiredComments.filter(rc => rc !== score)
    })
  }

  updateScoreOverridesField(score, val) {
    this.setState({
      scoreOverrides: {
        ...(this.state.scoreOverrides || {}),
        [score]: val
      }
    })
  }

  updateNotObserved(event) {
    this.setState({useNotObserved: event.target.checked});
  }

  updateNotResponsive(event) {
    this.setState({useNotResponsive: event.target.checked});
  }

  handleSubmit() {
    const scale = parseInt(this.state.scale || 3, 10);
    this.props.onSave({
      ratingPrompt: this.state.ratingPrompt,
      scale,
      notObservedPrompt: this.state.notObservedPrompt || 'N/O',
      notResponsivePrompt: this.state.notResponsivePrompt || 'N/R',
      defaultComments: this.state.defaultComments.filter(dfc => dfc.rating && dfc.comment),
      requiredComments: this.state.requiredComments.filter(score => score <= scale),
      useNotObserved: this.state.useNotObserved,
      useNotResponsive: this.state.useNotResponsive,
      overrideDefaultScores: true,
      scoreOverrides: this.scores.reduce((acc, score) => ({
        ...acc,
        [score]: (this.state.scoreOverrides || {})[score] || score.toString()
      }), {})
    });
  }

  render() {
    return (
      <Fragment>
        <Card className="mb-3 rating-multi-form-card">
          <h5 className="p-3">Evaluation rating field</h5>
          <Form className="m-3" noValidate onSubmit={event => this.handleSubmit(event)}>
            <FormGroup>
              <Label for="ratingPrompt">Competency title / prompt *</Label>
              <Input
                className={this.state.cssValidationClasses.ratingPrompt}
                id="ratingPrompt"
                name="ratingPrompt"
                onBlur={event => this.handleBlur(event)}
                onChange={event => this.handleInput(event)}
                placeholder="Header"
                type="textarea"
                rows={2}
                value={this.state.ratingPrompt}
              />
              <FormFeedback>{this.state.errorFeedback.ratingPrompt}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="scale">Rating scale *</Label>
              <Input
                className={this.state.cssValidationClasses.scale}
                id="scale"
                name="scale"
                type="number"
                min="1"
                max="10"
                onBlur={event => this.handleBlur(event)}
                onChange={event => this.handleInput(event)}
                value={this.state.scale}
              />
              <FormFeedback>{this.state.errorFeedback.scale}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Checkbox checked={this.state.useNotObserved !== false} label="Use N/O field?"
                        onChange={(checked) => this.updateNotObserved(checked)}/>
            </FormGroup>

            {this.state.useNotObserved !== false && (
                <FormGroup>
                  <Label for="notObservedPrompt">Not observed prompt</Label>
                  <Input
                    id="notObservedPrompt"
                    name="notObservedPrompt"
                    onBlur={event => this.handleBlur(event)}
                    onChange={event => this.handleInput(event)}
                    value={this.state.notObservedPrompt}
                  />
                </FormGroup>
            )}

            <FormGroup>
              <Checkbox checked={this.state.useNotResponsive !== false} label="Use N/R field?"
                        onChange={(checked) => this.updateNotResponsive(checked)}/>
            </FormGroup>

            {this.state.useNotResponsive !== false && (
                <FormGroup>
                  <Label for="notResponsivePrompt">Not responsive prompt</Label>
                  <Input
                    id="notResponsivePrompt"
                    name="notResponsivePrompt"
                    onBlur={event => this.handleBlur(event)}
                    onChange={event => this.handleInput(event)}
                    value={this.state.notResponsivePrompt}
                  />
                </FormGroup>
            )}

            <div className="mb-4">
              <div>Rating labels</div>
              {this.scores.map(score => (
                <FormGroup key={score}>
                  <Label for={`ratingOverride${score}`}>({score})</Label>
                  <Input
                    id={`ratingOverride${score}`}
                    name={`ratingOverride${score}`}
                    onChange={event => this.updateScoreOverridesField(score, event.target.value)}
                    placeholder={`Rating ${score} label`}
                    value={(this.state.scoreOverrides || {})[score] || ''}
                  />
                </FormGroup>
              ))}
            </div>

            <div className="rating-multi-default-comments">
              <label>Default comments for rating</label>
              {this.state.defaultComments.map((dfc, index) => (
                <FormGroup className="default-comments-item" key={index}>
                  <select className="form-control" value={dfc.rating} onChange={(e) => this.updateDefaultCommentRating(index, e.target.value)}>
                    <option value="">Select rating</option>
                    {this.scores.map(score => <option key={score} value={score}>{score}</option>)}
                  </select>
                  <Input type="textarea" placeholder="Enter default comment" value={dfc.comment} onChange={e => this.updateDefaultCommentComment(index, e.target.value)}/>
                  <button type="button" className="button small danger" onClick={() => this.removeDefaultComment(index)}>
                    <i className="fa fa-close"/>
                  </button>
                </FormGroup>
              ))}
              <div className="text-right">
                <button type="button" className="button small secondary" onClick={() => this.addDefaultComment()}>
                  <i className="fa fa-plus"/>
                  <span>Add comment for rating</span>
                </button>
              </div>
            </div>

            <div className="rating-multi-required-comments mb-3">
              <label>Select scores that have required comment</label>
              <div className="row">
                {this.scores.map(score => (
                  <div className="col" key={score}>
                    <Checkbox
                      label={score.toString()}
                      checked={this.isRequiredCommentChecked(score)}
                      onChange={e => this.onRequiredCommentCheck(score, e.target.checked)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <LoadingButton
                primary
                text="Save"
                isLoading={this.props.isLoading}
                disabled={this.state.isFormDisabled}
                onClick={event => this.handleSubmit(event)}
              />
            </div>
          </Form>
        </Card>
      </Fragment>
    );
  }
}

export default withValidator(EvaluationRatingFormCard, validatorConfig);
export {DEFAULT_SCORE_OVERRIDES};
